import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  DollarSign, 
  CheckCircle, 
  AlertCircle,
  Star,
  Zap,
  TrendingUp,
  Clock,
  Heart,
  Brain,
  Users,
  GraduationCap,
  Briefcase
} from 'lucide-react';
import { useGameStore } from '../store/gameStore';
import { DecisionOption } from '../types/game';

const DecisionModal: React.FC = () => {
  const {
    stats,
    money,
    makeDecision,
    currentAge,
    selectedDecision,
    isDecisionModalOpen,
    closeDecisionModal,
    spendMoney
  } = useGameStore();

  const [selectedOption, setSelectedOption] = useState<DecisionOption | null>(null);
  const [showConsequences, setShowConsequences] = useState(false);

  // Reset modal state when it closes
  React.useEffect(() => {
    if (!isDecisionModalOpen) {
      setSelectedOption(null);
      setShowConsequences(false);
    }
  }, [isDecisionModalOpen]);

  const handleOptionSelect = (option: DecisionOption) => {
    setSelectedOption(option);
    setShowConsequences(true);
  };

  const handleConfirmDecision = () => {
    if (selectedDecision && selectedOption) {
      // Allow debt - no affordability check needed
      makeDecision(selectedDecision.id, selectedOption.id);
      setSelectedOption(null);
      setShowConsequences(false);
      closeDecisionModal();
    }
  };

  const canAfford = (option: DecisionOption) => {
    // Always allow - debt is permitted
    return true;
  };

  const getAffordabilityColor = (option: DecisionOption) => {
    if (!option.cost) return 'text-slate-300';
    return canAfford(option) ? 'text-emerald-400' : 'text-red-400';
  };

  const meetsRequirements = (option: DecisionOption) => {
    if (!option.requirements) return true;
    return Object.entries(option.requirements).every(([stat, value]) => 
      stats[stat as keyof typeof stats] >= (value || 0)
    );
  };

  const getStatChangeIcon = (change: number) => {
    if (change > 0) return '↗️';
    if (change < 0) return '↘️';
    return '➡️';
  };

  const getStatIcon = (stat: string) => {
    const icons = {
      health: Heart,
      happiness: Brain,
      wealth: DollarSign,
      relationships: Users,
      education: GraduationCap,
      career: Briefcase,
    };
    return icons[stat as keyof typeof icons] || TrendingUp;
  };

  if (!isDecisionModalOpen || !selectedDecision) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={closeDecisionModal}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-slate-900 rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-slate-700"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center text-3xl backdrop-blur-sm">
                    {selectedDecision.icon || '🤔'}
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold mb-2">{selectedDecision.title}</h2>
                    <p className="text-blue-100 text-lg">{selectedDecision.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <div className="text-sm text-blue-200">Your Money</div>
                    <div className="text-2xl font-bold">${money.toLocaleString()}</div>
                  </div>
                  <button
                    onClick={closeDecisionModal}
                    className="p-3 hover:bg-white/20 rounded-2xl transition-colors"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 max-h-[60vh] overflow-y-auto">
            {!showConsequences ? (
              /* Options Selection */
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Choose Your Path</h3>
                  <p className="text-slate-400">Select an option to see the consequences</p>
                </div>
                
                <div className="grid gap-6">
                  {selectedDecision.options.map((option, index) => {
                    const canChoose = canAfford(option) && meetsRequirements(option);
                    
                    return (
                      <motion.div
                        key={option.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => canChoose && handleOptionSelect(option)}
                        className={`p-6 rounded-2xl border-2 transition-all duration-300 cursor-pointer ${
                          canChoose
                            ? 'border-blue-500/50 hover:border-blue-400 bg-slate-800/50 hover:bg-slate-800/70'
                            : 'border-slate-600 bg-slate-800/30 cursor-not-allowed opacity-60'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="text-xl font-bold text-white">
                                {option.title}
                              </h4>
                              {option.cost && (
                                <div className={`text-lg font-bold ${getAffordabilityColor(option)}`}>
                                  ${option.cost.toLocaleString()}
                                </div>
                              )}
                            </div>
                            <p className="text-slate-300 mb-4">{option.description}</p>
                            
                            {/* Debt Warning */}
                            {option.debtWarning && (
                              <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-3 mb-4">
                                <div className="flex items-center space-x-2">
                                  <div className="text-amber-400">⚠️</div>
                                  <p className="text-amber-200 text-sm font-medium">
                                    {option.debtWarning}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Stat Changes Preview */}
                        <div className="flex flex-wrap gap-3 mb-4">
                          {Object.entries(option.statChanges).map(([stat, change]) => {
                            const Icon = getStatIcon(stat);
                            return (
                              <div
                                key={stat}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium ${
                                  change && change > 0
                                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                    : change && change < 0
                                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                    : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
                                }`}
                              >
                                <Icon size={16} />
                                <span className="capitalize">{stat}</span>
                                <span className="font-bold">
                                  {change && change > 0 ? '+' : ''}{change}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Requirements */}
                        {option.requirements && (
                          <div className="bg-slate-700/50 rounded-xl p-4 mb-4">
                            <h5 className="text-sm font-semibold text-slate-300 mb-2">Requirements:</h5>
                            <div className="flex flex-wrap gap-2">
                              {Object.entries(option.requirements).map(([stat, value]) => {
                                const Icon = getStatIcon(stat);
                                const meets = stats[stat as keyof typeof stats] >= (value || 0);
                                return (
                                  <div
                                    key={stat}
                                    className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-xs ${
                                      meets
                                        ? 'bg-emerald-500/20 text-emerald-400'
                                        : 'bg-red-500/20 text-red-400'
                                    }`}
                                  >
                                    <Icon size={12} />
                                    <span className="capitalize">{stat}</span>
                                    <span>{value}+</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {!canChoose && (
                          <div className="flex items-center space-x-2 text-red-400 text-sm">
                            <AlertCircle size={16} />
                            <span>
                              {!canAfford(option) ? 'Insufficient funds' : 'Requirements not met'}
                            </span>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Consequences Preview */
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">Consequences Preview</h3>
                  <p className="text-slate-400">Review the impact of your choice</p>
                </div>

                {selectedOption && (
                  <div className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                    <h4 className="text-xl font-bold text-white mb-4">{selectedOption.title}</h4>
                    <p className="text-slate-300 mb-6">{selectedOption.description}</p>
                    
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {Object.entries(selectedOption.statChanges).map(([stat, change]) => {
                        const Icon = getStatIcon(stat);
                        return (
                          <div
                            key={stat}
                            className={`flex items-center justify-between p-4 rounded-xl ${
                              change && change > 0
                                ? 'bg-emerald-500/10 border border-emerald-500/30'
                                : change && change < 0
                                ? 'bg-red-500/10 border border-red-500/30'
                                : 'bg-slate-700/50 border border-slate-600'
                            }`}
                          >
                            <div className="flex items-center space-x-3">
                              <Icon size={20} className={change && change > 0 ? 'text-emerald-400' : change && change < 0 ? 'text-red-400' : 'text-slate-400'} />
                              <span className="text-white font-medium capitalize">{stat}</span>
                            </div>
                            <div className={`text-lg font-bold ${
                              change && change > 0 ? 'text-emerald-400' : change && change < 0 ? 'text-red-400' : 'text-slate-400'
                            }`}>
                              {change && change > 0 ? '+' : ''}{change}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {selectedOption.cost && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
                        <div className="flex items-center justify-between">
                          <span className="text-amber-400 font-medium">Cost:</span>
                          <span className="text-amber-400 text-xl font-bold">
                            ${selectedOption.cost.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex space-x-4">
                  <button
                    onClick={() => setShowConsequences(false)}
                    className="flex-1 px-6 py-3 bg-slate-600 hover:bg-slate-500 text-white rounded-xl font-medium transition-colors"
                  >
                    Back to Options
                  </button>
                  <button
                    onClick={handleConfirmDecision}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl font-medium transition-all duration-300 shadow-lg"
                  >
                    Confirm Decision
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DecisionModal;